import { useEffect, useState, type ReactElement } from 'react';

import { pillButton } from './pillButton.js';

const COPIED_FEEDBACK_MS = 2000;

type ShareActionsProps = {
  readonly url: string;
  readonly shareText: string;
};

const ACTION_BUTTON = pillButton('quiet');

const whatsappHref = (url: string, shareText: string): string => {
  const message = `${shareText} ${url}`;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
};

export const ShareActions = ({ url, shareText }: ShareActionsProps): ReactElement => {
  const [copied, setCopied] = useState(false);
  const hasShareSheet = typeof navigator.share === 'function';

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [copied]);

  const handleCopy = (): void => {
    const copy = async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch {
        setCopied(false);
      }
    };
    void copy();
  };

  const handleShare = (): void => {
    const share = async (): Promise<void> => {
      try {
        await navigator.share({ title: shareText, url });
      } catch {
        // A dismissed share sheet is a normal outcome, not an error to report.
      }
    };
    void share();
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button type="button" onClick={handleCopy} className={ACTION_BUTTON}>
        {copied ? 'Copied' : 'Copy link'}
      </button>
      {hasShareSheet ? (
        <button type="button" onClick={handleShare} className={ACTION_BUTTON}>
          Share
        </button>
      ) : null}
      <a
        href={whatsappHref(url, shareText)}
        target="_blank"
        rel="noreferrer"
        className={ACTION_BUTTON}
      >
        WhatsApp
      </a>
    </div>
  );
};
